import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

type ToastStatus = 'success' | 'error' | 'info' | 'warning';

interface ToastOptions {
    title: string;
    description?: string;
    status?: ToastStatus;
    duration?: number;
    bottomOffset?: string;
}

interface ToastItem extends ToastOptions {
    id: number;
    bottomOffset?: string;
}

interface ToastContextShape {
    toast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextShape | undefined>(undefined);

const statusConfig: Record<ToastStatus, { icon: string; bg: string; border: string; iconColor: string }> = {
    success: {
        icon: 'check_circle',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/30',
        iconColor: 'text-emerald-400',
    },
    error: {
        icon: 'error',
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        iconColor: 'text-red-400',
    },
    info: {
        icon: 'info',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        iconColor: 'text-blue-400',
    },
    warning: {
        icon: 'warning',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        iconColor: 'text-amber-400',
    },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const idRef = useRef(0);

    const toast = useCallback((options: ToastOptions) => {
        const id = ++idRef.current;
        const duration = options.duration ?? 3000;
        setToasts(prev => [...prev, { ...options, id }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
    }, []);

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}
            {/* Toast container */}
            <div className="fixed left-1/2 -translate-x-1/2 z-[9999] flex flex-col-reverse gap-2 pointer-events-none w-full max-w-sm px-4" style={{ bottom: toasts.length > 0 ? (toasts[toasts.length - 1].bottomOffset ?? '1.5rem') : '1.5rem' }}>
                {toasts.map((t) => {
                    const cfg = statusConfig[t.status || 'info'];
                    return (
                        <div
                            key={t.id}
                            className={`pointer-events-auto ${cfg.bg} ${cfg.border} border backdrop-blur-xl rounded-xl px-4 py-3 flex items-center gap-3 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-200`}
                        >
                            <span className={`material-symbols-outlined ${cfg.iconColor} text-[20px] shrink-0`}>{cfg.icon}</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-txt-heading text-sm font-semibold leading-tight">{t.title}</p>
                                {t.description && (
                                    <p className="text-txt-secondary text-xs mt-0.5 leading-snug">{t.description}</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
};

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx.toast;
}
